import {
  getRuntimeConfig,
  onRuntimeConfigChange,
} from './utils/runtimeConfig';

const getBrowserLocation = () => (typeof window !== 'undefined' && window.location ? window.location : null);

const isLocalhostLocation = () => {
  const loc = getBrowserLocation();
  if (!loc) return false;
  return loc.hostname === 'localhost' || loc.hostname === '127.0.0.1' || loc.hostname === '[::1]';
};

function resolveServerURI(runtimeConfig = getRuntimeConfig()) {
  if (runtimeConfig?.apiBaseUrl) {
    return runtimeConfig.apiBaseUrl;
  }
  if (typeof process !== 'undefined' && process.env?.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.trim().replace(/\/+$/, '');
  }
  const loc = getBrowserLocation();
  if (isLocalhostLocation()) {
    const protocol = loc?.protocol === 'https:' ? 'https:' : 'http:';
    return `${protocol}//localhost:8080`;
  }
  if (loc?.origin && loc.origin !== 'null') {
    return loc.origin.replace(/\/+$/, '');
  }
  return '';
}

function resolveAppEnv(runtimeConfig = getRuntimeConfig()) {
  if (runtimeConfig?.appEnv) {
    return runtimeConfig.appEnv;
  }
  if (typeof process !== 'undefined' && process.env?.REACT_APP_APP_ENV) {
    return process.env.REACT_APP_APP_ENV.trim();
  }
  return isLocalhostLocation() ? 'local' : null;
}

function resolveDevFeaturesEnabled(runtimeConfig = getRuntimeConfig()) {
  const resolvedEnv = resolveAppEnv(runtimeConfig);
  if (resolvedEnv) {
    return resolvedEnv === 'local';
  }
  return isLocalhostLocation();
}

export let ServerURI = resolveServerURI();
export let appEnv = resolveAppEnv();

export const versionNum = '110426.001';

// Dev features - enabled only for local development unless explicitly configured
export let devFeaturesEnabled = resolveDevFeaturesEnabled();

onRuntimeConfigChange((nextConfig) => {
  ServerURI = resolveServerURI(nextConfig);
  appEnv = resolveAppEnv(nextConfig);
  devFeaturesEnabled = resolveDevFeaturesEnabled(nextConfig);
});


// global variables used across Featherglass

export const companyName = 'Featherglass Health, Inc';

export const appName = 'Featherglass';

// determines which fields to reference for history processing
export const historyFields = ['documentDate', 'createdAt', 'modifiedAt', 'document_summary', 'plan', 'after_visit_summary', 'medications', 'diagnostic_impressions_data', 'interval_history', 'substance_use', 'family_history', 'allergies', 'social_history', 'trauma_history', 'psychiatric_history','medical_history'];

export const historyDepth = 24; // determine how many previous records are sent with history data

export const minimumPatientAge = 5; // minimum age in years for patient creation
 
export const recordingConfirmationMessage = `Please confirm that both you and the patient agree to this recording.`

export const recordingConfirmedMessage = `This record was generated using a HIPAA-compliant AI system with the patient's informed consent. The clinician has carefully reviewed the content to ensure accuracy, as AI may occasionally produce errors.`

export const cancelRecordingMessage = `Cancelling deletes this entire recording. Are you sure you want to cancel?`

export const streamingSaveDebounce = 3000; // time between recording updates

export const notificationDebounce = 10000; // 10 seconds - minimum time between identical notifications

export const recordingResetTime = 5400000; // reconnect the recording after this time (90 minutes)

export const recordingTimeLimit = 10800000; // 180 minutes (3 hours)

export const maximumSessionDuration = 28800000; // 8 hours in milliseconds

export const tagWeight = 0.75; // tag threshold for display

export const documentsPerPage = 50; // number of documents per page

export const MIN_DOCUMENTS_FOR_EXPORT = 4; // minimum documents recommended before generating an export summary

export const minimumTextLength = 1024; // minimum text character length for processing (upload / text-paste paths)
export const minimumTranscriptWordCount = 100; // minimum word count for recording transcripts — below this we reject with a user notification

export const signalStrengths = [100, 160, 220]; // [100,150,200] roundtrip times in ms for signal strength

export const googleAnalytics = "G-2BDD759GEL";

export const recordingSaveInterval = 15000; // 15 seconds between streaming saves

export const silenceThreshold = 1; // Volume level below which is considered silence (0-9 scale)

export const silenceWarningDelay = 10000;

// Pre-recorded mode constants
export const chunkDurationRamp = [60000, 90000, 120000, 180000]; // ms per chunk: 60s → 90s → 2min → 3min
export const preRecordedChunkDurationMs = 300000;  // 5 minutes — legacy fallback when rolling transcription is off
export const preRecordedOverlapMs = 5000;           // 5 seconds overlap between chunks
export const preRecordedMaxRetries = 3;
export const preRecordedRollingTranscription = true; // Kill switch: false = batch-after-stop (legacy behavior)

// Chunk QC thresholds (audio quality gates — Phase 2)
export const chunkQcVeryLowWpm = 10;     // words/min — near-nothing, likely dead mic or too far away
export const chunkQcLowWpm = 30;         // words/min — sparse speech, possibly usable but check mic

// Volume monitoring + escalating warnings (audio quality gates — Phase 3)
export const lowVolumeThreshold = 1;             // volume level 0–9; only true silence/near-silence (0-1) triggers warnings
export const lowVolumeWarningDelay = 30000;      // 30s sustained low volume → StatusBand warning
export const lowVolumeToastDelay = 120000;       // 2 min sustained → first toast
export const lowVolumeToastRepeat = 300000;      // repeat toast every 5 min while still low
export const clippingThreshold = 9;              // volume level 0–9 above which = clipping
export const clippingWarningDelay = 5000;        // 5s sustained clipping → warning
export const volumePollingInterval = 2000;       // 2s polling interval for volume checks

// Post-recording quality gate (audio quality gates — Phase 4)
export const lowConfidenceThreshold = 0.75;      // unified per-word confidence floor
export const minimumWordCount = 20;              // below = block processing
